import { Injectable } from '@angular/core';
import { PlatformService } from './platform.service';
import { DialogsService } from './dialogs.service';

declare global {
  interface Window {
    adngin?: any;
    destroyRefreshTopAd?: any;
    adconsent: (
      arg0: string,
      arg1?: any,
      arg2?: (consent: any, success: any) => any
    ) => void;
    __tcfapi: (
      arg0: string,
      arg1: number,
      arg2: (tcData: any, success: any) => void
    ) => void;
    snigelPubConf: {
      adengine: {
        activeAdUnits: string[];
      };
    };
    gtag?: any;
    dataLayer?: any[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class AdsService {
  constructor(
    private platform: PlatformService,
    private dialog: DialogsService
  ) {}

  /**
   * Loads ad script and sets active ad units.
   * For Premium users ad script needs to be loaded so we can get consent, but active units should be empty so no ads appear.
   * For non premium users, ad script needs to be loaded with correct active ad units.
   */
  async loadAdScriptAndDetectAdBlocker(isPremium: boolean) {
    // set active ad units
    this.setActiveAdUnits(isPremium);

    // load ad script

    if (!this.platform.isInBrowser()) return;
    const href = window.location.href;
    let src = '';
    if (href.includes('staging')) {
      console.log('Loading STAGING ads script');
      src =
        'https://staging-cdn.snigelweb.com/adengine/wheretaken.teuteuf.fr/loader.js';
    } else {
      console.log('Loading LIVE ads script');
      src =
        'https://cdn.snigelweb.com/adengine/wheretaken.teuteuf.fr/loader.js';
    }
    let node = document.createElement('script');
    node.src = src;
    node.type = 'text/javascript';
    node.async = true;
    node.setAttribute('data-cfasync', 'false');
    if (
      !document.querySelector(
        `script[src*="adengine/wheretaken.teuteuf.fr/loader.js"]`
      )
    ) {
      document.getElementsByTagName('head')[0].appendChild(node);
    }

    // check for ad block
    // const adBlockerDetected = await this.detectAdBlocker();
    // if (adBlockerDetected) this.showAntiAdBlockWarning();
  }

  getActiveAdUnits(): string[] {
    let breakpoint = 'xsmall';

    const w = window.innerWidth;

    breakpoint = w >= 768 ? 'small' : breakpoint;
    breakpoint = w >= 960 ? 'medium' : breakpoint;
    breakpoint = w >= 1280 ? 'large' : breakpoint;
    breakpoint = w >= 1400 ? 'xlarge' : breakpoint;

    let activeUnits: string[] = [];

    switch (breakpoint.toLowerCase()) {
      case 'xsmall': // Small Mobile
      case 'small': // Portrait Tablet / Large Mobile
      case 'medium':
        activeUnits = ['top_banner']; // Mobile Ad Units
        break;
      case 'large':
      case 'xlarge':
        activeUnits = ['sidebar_left', 'video'];
        break;
      default:
        activeUnits = [];
    }

    window.snigelPubConf = {
      adengine: {
        activeAdUnits: activeUnits,
      },
    };
    return activeUnits;
  }

  setActiveAdUnits(isPremium: boolean): void {
    if (isPremium) {
      window.snigelPubConf = {
        adengine: {
          activeAdUnits: [''],
        },
      };
      return;
    }

    const activeUnits = this.getActiveAdUnits();

    window.snigelPubConf = {
      adengine: {
        activeAdUnits: activeUnits,
      },
    };
  }

  /**
   * Check for consent through Snigel, then set GA consent
   */
  checkForConsent(): void {
    const clearCookies = () => this.clearAnalyticsCookies();

    if (typeof window !== 'undefined') {
      window.addEventListener('adnginLoaderReady', function () {
        window.adconsent &&
          window.adconsent('getConsent', null, function (consent, success) {
            if (success) {
              if (consent.fullConsent) {
                console.log('########### yas consent');
                window.gtag('consent', 'update', {
                  ad_storage: 'granted',
                  analytics_storage: 'granted',
                  ad_user_data: 'granted',
                  ad_personalization: 'granted',
                });
              } else {
                console.log('########### naur consent');
                clearCookies();
              }
            }
          });
      });
    }
  }

  clearAnalyticsCookies() {
    const teuteufCookieNames = ['_gid'];

    // delete all _ga cookies
    document.cookie
      .split(';')
      .map((c) => c.trim())
      .forEach((cookie) => {
        const name = cookie.split('=')[0];
        if (name.startsWith('_ga')) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.teuteuf.fr`;
        }
      });

    // delete other analytics cookies
    teuteufCookieNames.forEach((name) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.teuteuf.fr`;
    });
  }

  destroyTopAd() {
    if (typeof window.destroyRefreshTopAd === 'function') {
      try {
        window.destroyRefreshTopAd();
      } catch (e) {
        console.error(e);
      }
    }
  }

  startAuction(adName: string, device: string) {
    if (device === 'desktop' && window.innerWidth < 1280) {
      return;
    } else if (device === 'mobile' && window.innerWidth >= 1280) {
      return;
    }
    setTimeout(() => {
      if (typeof window.adngin.cmd === 'object') {
        try {
          if (window.adngin && window.adngin.adnginLoaderReady) {
            console.log('Starting auction for ' + adName);
            window.adngin.cmd.startAuction([adName]);
          }
        } catch (e) {
          console.error(e);
        }
      }
    }, 500);
  }

  async detectBraveBrowser(): Promise<boolean> {
    if ((navigator as any).brave) {
      return (navigator as any).brave
        .isBrave()
        .then((isBrave: boolean) => isBrave);
    }
    return false;
  }

  async detectAdBlocker(): Promise<boolean> {
    const isBrave = await this.detectBraveBrowser();
    if (isBrave) return true;

    return new Promise((resolve) => {
      let detected = false;

      // 1. attempt to load an ad script
      const testScript = document.createElement('script');
      testScript.src =
        'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
      testScript.type = 'text/javascript';
      testScript.async = true;

      testScript.onerror = () => {
        if (!detected) {
          detected = true;
          resolve(true);
        }
      };

      document.body.appendChild(testScript);

      // 2. check if the request was actually made
      setTimeout(() => {
        const entries = performance.getEntriesByType('resource');
        const adRequest = entries.find((entry) =>
          entry.name.includes('pagead2.googlesyndication.com')
        );

        if (!adRequest && !detected) {
          detected = true;
          resolve(true);
        }

        document.body.removeChild(testScript);

        // no ad blocker detected
        if (!detected) {
          resolve(false);
        }
      }, 2000);
    });
  }

  showAntiAdBlockWarning() {
    // if user has seen toast in the last 3 days, don't show toast
    const storedLastSeenAdBlockerToast =
      localStorage.getItem('ad-blocker-toast');
    const lastSeenToast = storedLastSeenAdBlockerToast
      ? JSON.parse(storedLastSeenAdBlockerToast)
      : null;
    if (lastSeenToast && Date.now() - lastSeenToast < 1000 * 60 * 60 * 24 * 3) {
      return;
    }

    this.dialog.openAntiAdBlockWarning();
    localStorage.setItem('ad-blocker-toast', JSON.stringify(Date.now()));
  }
}
